<template>
  <!--
    O iframe e montado UMA vez por sessao e so se esconde: fechar o painel ou
    entrar numa tela sem menu nao pode desmonta-lo, porque isso derruba o tempo
    real e o selo de mensagens nao lidas.
  -->
  <div
    v-if="session"
    data-test="support-widget"
  >
    <section
      v-show="isPanelVisible"
      class="support-widget"
      :class="panelClasses"
      :style="panelStyle"
      role="dialog"
      :aria-label="$t('supportWidget.supportWidgetHostUi.panelLabel')"
      :aria-hidden="panelAriaHidden"
      data-test="support-widget-panel"
    >
      <iframe
        ref="frame"
        :key="session.id"
        :src="frameUrl"
        :title="$t('supportWidget.supportWidgetHostUi.frameTitle')"
        allow="clipboard-write"
        class="support-widget__frame"
        data-test="support-widget-frame"
      ></iframe>
    </section>

    <SupportWidgetLauncher
      v-if="isLauncherShown"
      :pending="pending"
      :has-attention="hasAttention"
      :offset-style="launcherStyle"
      @open="openPanel"
      @dismiss="dismissLauncher"
    />
  </div>
</template>

<script>
// Components
import SupportWidgetLauncher from '@/views/support/SupportWidgetLauncher.vue';

// Mixins
import mixinMessage from '@/mixin/mixin-message';
import mixinSupportWidgetSession from '@/mixin/mixin-support-widget-session';
import mixinSupportWidgetPlacement from '@/mixin/mixin-support-widget-placement';

// Constants
import { OPTIONS_STORE_SUPPORT_WIDGET } from '@/vuex/module/supportWidget';

// Utils
import { widgetFrameUrl } from '@/utils/support-widget-bridge';

import { mapGetters } from 'vuex/dist/vuex.common.js';

/**
 * Chat de suporte (DeepDesk) dentro do TechCambio: o portal num iframe, num
 * painel no canto inferior direito, e a bolinha que o reabre. Abre pelo item
 * "Suporte" do menu do usuario. A conversa com o iframe esta em
 * mixin-support-widget-session; a posicao na tela, em
 * mixin-support-widget-placement.
 *
 * O painel e a bolinha anulam o zoom global do app (`--tc-zoom`): o chat e o
 * mesmo em todos os produtos e tem de sair no tamanho real, 400x680, com o
 * conteudo do iframe na escala em que foi desenhado.
 */
export default {
  name: 'SupportWidgetHost',

  components: { SupportWidgetLauncher },

  mixins: [mixinMessage, mixinSupportWidgetSession, mixinSupportWidgetPlacement],

  /* Padrao para a montagem isolada dos testes, que nao tem o App por cima. */
  inject: {
    appData: { default: () => ({}) }
  },

  props: {
    /* Falso nas telas sem menu (primeiros passos, visualizacao de arquivo): o
       chat fica guardado, sem desmontar, e volta ao sair delas. */
    isShellVisible: { type: Boolean, default: true }
  },

  computed: {
    ...mapGetters({
      isAvailable: OPTIONS_STORE_SUPPORT_WIDGET.GETTERS.IS_AVAILABLE,
      isOpen: OPTIONS_STORE_SUPPORT_WIDGET.GETTERS.IS_OPEN,
      isLauncherVisible: OPTIONS_STORE_SUPPORT_WIDGET.GETTERS.IS_LAUNCHER_VISIBLE,
      pending: OPTIONS_STORE_SUPPORT_WIDGET.GETTERS.PENDING,
      attention: OPTIONS_STORE_SUPPORT_WIDGET.GETTERS.ATTENTION,
      request: OPTIONS_STORE_SUPPORT_WIDGET.GETTERS.REQUEST
    }),

    frameUrl() {
      return widgetFrameUrl(this.session.portalOrigin);
    },

    isPanelVisible() {
      return this.isOpen && this.isShellVisible;
    },

    isLauncherShown() {
      return this.isLauncherVisible && this.isShellVisible;
    },

    hasAttention() {
      return this.attention > 0;
    },

    panelAriaHidden() {
      return String(!this.isPanelVisible);
    },

    isKeyboardAware() {
      return this.isPanelVisible && this.isMobile;
    }
  },

  watch: {
    'request.seq': 'handleRequest',

    /* Resposta ou aviso novo do suporte: a bolinha aparece sozinha, com o selo.
       So quando a conta sobe: quem dispensou nao a ve voltar pelo que ja tinha. */
    pending(current, previous) {
      if (current > previous) this.summonLauncher(true);
    },

    isKeyboardAware: 'trackVisibleArea'
  },

  methods: {
    /* Pedido do menu, da bolinha ou do aviso de mensagem. O painel nunca abre
       sozinho: sempre ha um clique por tras. */
    handleRequest() {
      if (!this.bridge || !this.isAvailable) return;

      if (this.request.open) this.openFromRequest();
      else this.bridge.setOpen(false);
    },

    /* Aberto pelo menu, o chat passa a ter a bolinha: fechar o painel volta a ela. */
    openFromRequest() {
      this.summonLauncher(true);

      if (this.request.route) this.bridge.navigate(this.request.route);

      this.bridge.setOpen(true);
    },

    openPanel() {
      this.$store.commit(OPTIONS_STORE_SUPPORT_WIDGET.MUTATIONS.REQUEST_OPEN);
    },

    dismissLauncher() {
      this.summonLauncher(false);
    }
  }
};
</script>

<style lang="scss" scoped>
.support-widget {
  position: fixed;
  overflow: hidden;
  background: var(--tc-surface);
  zoom: calc(1 / var(--tc-zoom));
}

.support-widget--desktop {
  right: 20px;
  bottom: 20px;
  z-index: 7;
  width: 400px;
  height: 680px;
  max-height: calc(100vh - 88px);
  border: 1px solid var(--tc-line);
  border-radius: var(--tc-radius-overlay);
}

.support-widget--mobile {
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 220;
}

.support-widget__frame {
  display: block;
  width: 100%;
  height: 100%;
  border: 0;
}
</style>
